import React from "react";
import { toast } from "sonner";
import { Mail, Linkedin } from "lucide-react";
import { MaskedWords, Reveal } from "../components/Reveal";
import { ResumeButton } from "../components/ResumeButton";
import { PROFILE } from "../data/content";
import { Star, Arrow } from "../components/Scribbles";

export default function Contact() {
  const mailHref = `mailto:${PROFILE.email}?subject=${encodeURIComponent(
    "Glad to connect"
  )}&body=${encodeURIComponent(PROFILE.connectMessage)}`;

  const handleLinkedIn = async () => {
    try {
      await navigator.clipboard.writeText(PROFILE.connectMessage);
      toast("Message copied to clipboard!", {
        description: `Paste "${PROFILE.connectMessage}" into LinkedIn — opening now.`,
      });
    } catch (e) {
      toast("Opening LinkedIn", {
        description: `Send Arya this: "${PROFILE.connectMessage}"`,
      });
    }
    window.open("https://www.linkedin.com/messaging/compose/", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="px-5 md:px-10 pt-32 pb-28 min-h-screen flex flex-col" data-testid="contact-page">
      <header className="max-w-5xl mb-16 relative">
        <p className="font-handwritten text-3xl text-terracotta mb-4 rotate-[-2deg]">say hello</p>
        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-[9vw] uppercase tracking-tighter text-ink leading-none">
          <MaskedWords text="Let's connect" />
        </h1>
        <Star size={40} className="absolute top-4 right-2 hidden md:block" />
      </header>

      <div className="grid md:grid-cols-2 border-t-2 border-ink max-w-6xl">
        {/* MAIL */}
        <Reveal className="border-b-2 md:border-b-0 md:border-r-2 border-ink p-8 md:p-14 group hover:bg-ink transition-colors duration-500">
          <a href={mailHref} className="block" data-testid="contact-mail-button">
            <Mail size={40} className="text-terracotta mb-8" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground group-hover:text-sand/60 mb-3">
              Option 01 — Drop a line
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink group-hover:text-sand transition-colors duration-500">
              Write me an email
            </h2>
            <p className="mt-4 text-base text-muted-foreground group-hover:text-sand/70 transition-colors duration-500">
              Opens your mail app addressed to <span className="text-ink group-hover:text-sand">{PROFILE.email}</span>, pre-filled with a friendly hello.
            </p>
          </a>
        </Reveal>

        {/* LINKEDIN */}
        <Reveal delay={0.1} className="p-8 md:p-14 group hover:bg-ink transition-colors duration-500">
          <button
            onClick={handleLinkedIn}
            className="block text-left w-full"
            data-testid="contact-linkedin-button"
          >
            <Linkedin size={40} className="text-terracotta mb-8" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground group-hover:text-sand/60 mb-3">
              Option 02 — Slide into the DMs
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink group-hover:text-sand transition-colors duration-500">
              Message on LinkedIn
            </h2>
            <p className="mt-4 text-base text-muted-foreground group-hover:text-sand/70 transition-colors duration-500">
              Copies “{PROFILE.connectMessage}” to your clipboard and opens LinkedIn — just paste & send.
            </p>
          </button>
        </Reveal>
      </div>

      {/* RESUME + LOCATION */}
      <Reveal delay={0.2} className="mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-10 max-w-6xl">
        <ResumeButton testId="contact-download-resume" variant="light" />
        <div className="flex items-center gap-4">
          <Arrow size={54} className="rotate-180 hidden md:block" />
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Based in</p>
            <p className="font-serif text-4xl md:text-5xl text-ink">{PROFILE.location}</p>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
